import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useTheme } from "@mui/material/styles";
import { Handle, Position } from "@xyflow/react";
import React from "react";
import { getNodeCardSx } from "./nodeCardStyles";

interface INodeListCardProps {
    children: React.ReactNode;
    isLoading?: boolean;
    isSelected?: boolean;
    /** Optional fixed minWidth; the card grows to fit content (`width: max-content`). */
    minWidth?: number;
    nodeColor: string;
    source?: "cs" | "d365";
    /** Target handle id. Only needed when the graph references a non-default handle (e.g. FM route top). */
    targetHandleId?: string;
    targetPosition?: Position;
    /** Optional fixed width. Mutually exclusive with `minWidth` in practice. */
    width?: number;
}

/**
 * Shared card scaffold used by every "list" explorer node.
 * Wraps the React Flow handles, the `.explorer-list-node` CSS hook, and the Card/CardContent
 * styling that was previously copy-pasted across 7 node components.
 */
export default function NodeListCard({
    children,
    isLoading,
    isSelected,
    minWidth,
    nodeColor,
    source,
    targetHandleId,
    targetPosition = Position.Left,
    width,
}: INodeListCardProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    return (
        <>
            <Handle
                type="target"
                position={targetPosition}
                id={targetHandleId}
                style={{ opacity: 0 }}
            />
            <div className="explorer-list-node">
                <Card
                    elevation={0}
                    sx={{
                        ...getNodeCardSx({ nodeColor, isSelected, isDark, isLoading, source, width }),
                        ...(minWidth !== undefined ? { minWidth, width: "max-content" } : {}),
                        position: "relative",
                        zIndex: 1,
                        overflow: "visible",
                    }}
                >
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1 } }}>
                        {children}
                    </CardContent>
                </Card>
            </div>
            <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
        </>
    );
}
